// How many people are on each team, counted from the church's membership
// rows with the same rule the organizations service uses for the stored count
// and the size limit (TeamMembersFilter): on the team, active, and not still
// waiting for approval. The service used to keep that count with +1/-1
// updates that drifted; it now derives it, and this keeps the UI on the same
// definition even against an older deployment.
import { useCallback, useEffect, useState } from 'react';
import { membershipsApi } from '@app/api/organization';
import { onMembershipChanged } from '@app/utils/membershipEvents';
import { logger } from '@app/utils/logger';

interface Row {
  userId: string;
  isActive?: boolean;
  churchState?: unknown;
  group?: { groupId?: string } | null;
}

/** Active and not waiting for approval (Pending is 1 / "Pending"). Legacy rows with no state, or None, count. */
const onTeamCount = (r: Row) => {
  if (r.isActive === false) return false;
  const s = r.churchState;
  return !(s === 1 || (typeof s === 'string' && s.toLowerCase() === 'pending'));
};

/** teamId → number of distinct people counted on that team. */
export function countByTeam(rows: Row[]): Record<string, number> {
  const seen = new Set<string>();
  const counts: Record<string, number> = {};
  for (const r of rows) {
    const team = r.group?.groupId;
    if (!team || !onTeamCount(r)) continue;
    const key = `${r.userId}:${team}`;
    if (seen.has(key)) continue;
    seen.add(key);
    counts[team] = (counts[team] ?? 0) + 1;
  }
  return counts;
}

export function useTeamHeadcounts(churchId: string | null | undefined) {
  const [counts, setCounts] = useState<Record<string, number> | null>(null);

  const load = useCallback(async () => {
    if (!churchId) return;
    try {
      setCounts(countByTeam(await membershipsApi.getChurchMembers(churchId)));
    } catch (err) {
      logger.warn('Team headcounts unavailable', err);
      setCounts(null);
    }
  }, [churchId]);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => onMembershipChanged(() => void load()), [load]);

  return counts;
}
